import m from "mithril";
import _ from "underscore";
import preload from "./preload";
import {Questionary} from "../model/questionary";
import {Form} from "../model/form/form";
import {Application} from "./app";
import Cell from "../model/form/cell";
import customization = require('../customization/customization');
import ICustomForm from "../customForms/ICustomForm";
import CustomFormFactory from "../customForms/CustomFormFactory";
import ICustomFormSettings from "../customForms/ICustomFormSettings";
import Controls from "./components/controls";
import StickyElement from "./utils/StickyElement";

export class FormView {
    private questionary: Questionary;
    private readonly form: Form;
    private readonly app: Application;
    private readonly customization: any;
    private loading: boolean;
    private initialized: boolean;
    private readonly customForm: ICustomForm|null = null;
    private readonly controls: Controls;
    private readonly customFormSetting: ICustomFormSettings;
	private stickyElement?: StickyElement;


    constructor(questionary: Questionary, form: Form, app: Application) {
        this.questionary = questionary;
        this.form = form;
        this.app = app;
        this.customization = customization.getForm(questionary, form);
        this.loading = true;
        this.form.customForm = null; // TODO remove from view cycle

        this.customFormSetting = {
            form: this.form,
            app: this.app,
            formHtmlClass: () => this.getFormCssClass(),
            prevForm: () => {
                this.questionary.prev()
            },
            nextForm: () => {
                const errors = this.form.collectValidationErrors();
                if (errors.length && this.customForm) {
                    this.customForm.onValidationErrors(errors);
                } else {
                    this.questionary.next()
                }
            },
            selectLanguage: () => {
                this.questionary.dictionary!.locale = undefined;
                m.redraw();
            }
        };

        if (CustomFormFactory.parseFormType(form.class) !== '') {
            try {
                // Try to create custom form, if in property class are defined multiple classes, get first one
                const customForm = CustomFormFactory.create(form.class.split(' ')[0], this.customFormSetting);

                // Check requirements
                if (customForm.checkRequirements()) {
                    this.customForm = customForm;
                    this.form.customForm = customForm;
                }
            } catch (e) {
                console.log(e);
                this.customForm = null;
            }
        }

        preload(this.form.resourcesToPreload()).then(() => {
            this.loading = false;
            this.form.startTiming();
            if (this.questionary.eventReporter) {
                this.questionary.eventReporter.reportFormShown({formId: this.form.id});
            }
            m.redraw();
        }).then(() => this.form.checkTiming(), () => this.form.checkTiming());

        this.controls = new Controls(this.customFormSetting, false);

        this.initialized = true;
    }

    public isCurrentForm(): boolean {
        return this.form === this.questionary.currentForm;
    }

    public globalKeyPressed(e: KeyboardEvent) {
        this.form.grid.eachControl(c => c.globalKeyPressed(e));
    };

    public getFormCssClass(): string {
        return 'form appear-right ' +
            (this.loading ? 'loading ' : '') +
            (this.form.isSimpleForm() ? ' simple-form ' : '') +
            (this.questionary.currentForm ? this.questionary.currentForm.class : '');
    }

    public view() {
        // TODO remove from view cycle
        this.form.grid.updateActiveCells();

        if (this.customForm) {
            return m(this.customForm, {});
        }

        const renderGrid = () => this.form.grid.layout.map(
            (rowArr, row) => {
                if (this.form.grid.isRowVisible(row)) {
                    let attrs: {[name:string]: string} = {};
                    if (this.form.grid.isLabelRow(row)) {
                        attrs['class'] = 'label-row';
                    }
                    const height = this.form.grid.rowHeight(row);
                    if (height > 0) {
                        attrs['style'] = `height: ${height}px`;
                    }
                    return m('tr', attrs,
                        rowArr.map((cell, column) => (this.form.grid.isColumnVisible(column) && cell) ? this.renderCell(cell) : null)
                    );
                }
            }
        );

        const formContent = this.customization
            ? this.customization.view()
            : m('table', [
                m('tbody', renderGrid())
            ]);

        const messages = this.form.validateResult.messages.length > 0 ? this.form.validateResult.messages : [this.app.messages.get('validateMessage')];

        const gridDimensionClass = this.form.grid ? ('.grid-columns-'+this.form.grid.columns+'.grid-rows-'+this.form.grid.rows) : '';
        const fixedHeader = this.form.class.includes('fixedHeader');
        const fixedHeaderRegExp = /fixedHeader-\d+/.exec(this.form.class);
        const fixedHeaderRow = fixedHeaderRegExp ? Number(fixedHeaderRegExp[0].split('-')[1]) : 1;

        return [
            m(this.app.header),
            m('div', {
                'class': this.getFormCssClass()
            }, _.flatten([
                m('.loader'),
                m('.grid'+gridDimensionClass, {
					oncreate: vnode => {
                        if (fixedHeader) {
                            const $table = $(vnode.dom).find('table').first();
                            const $stickyRow = $table.find(`tr:nth-child(${fixedHeaderRow})`);
                            const $stickyTarget = $(vnode.dom).find(`.stickyTarget table`)
                            this.stickyElement = new StickyElement(window, vnode.dom, $stickyTarget[0]);
                            this.stickyElement?.stick( $stickyRow[0], $table[0] );
                        }
					},
					onremove: (): any => {
						this.stickyElement?.destroy();
                        this.stickyElement = undefined;
					},
				}, [
                    formContent,
                    m('div', {'class': 'error-alert' + (this.form.validateResult.valid ? ' hidden' : '')},
                        _.map(messages, (msg: string) => m('div', m.trust(this.questionary.renderText(msg))))),
					m('.stickyTarget', {}, [
						m('table'),
					])
                ]),
                m(this.controls),
            ])),
        ];

    }

    private renderCell(cell: Cell) {
        let cssClass: string = cell.cssClass || "";
        if( cell.borders ) {
            cssClass += " " + cell.borders.classes();
        }

        return m('td', {
                colspan: cell.getColSpan(),
                rowspan: cell.getRowSpan(),
                'class': cssClass.trim(),
                style: `width: ${cell.width}px`
            },
            m('div', {
                    'class': 'cell' +
                    (cell.valid ? '' : ' invalid') +
                    (cell.enabled ? '' : ' disabled') +
                    (cell.visible ? '' : ' hidden') +
                    (cell.textAlign ? ' textalign-' + cell.textAlign : '')
                },
                _(cell.cellItems).map(cellItem => cellItem.getView())
            )
        );
    }

    public customFormRewriteControls(): boolean {
        return Boolean(this.customForm && this.customForm.rewriteFormControls());
    }

}
